import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Send, Check, Phone, Mail, MapPin, Loader2, Download, FileText, ShieldCheck } from "lucide-react";
import { COMPANY, PROJECT_TYPES } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
    name: "",
    company: "",
    email: "",
    phone: "",
    project_type: "",
    details: "",
};

export const Contact = () => {
    const [form, setForm] = useState(INITIAL);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.project_type || !form.details) {
            toast.error("Please fill in all required fields.");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API}/contact`, form);
            setSuccess(true);
            setForm(INITIAL);
            toast.success("Thanks — your inquiry has been received.", {
                description: data.email_sent
                    ? "Our team will follow up within one business day."
                    : "We've logged it and will follow up shortly.",
            });
        } catch (err) {
            console.error(err);
            toast.error("Submission failed. Please email " + COMPANY.email);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative bg-bone py-24 md:py-32 border-t border-line"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
                    {/* Left column */}
                    <div className="lg:col-span-5 reveal">
                        <div className="text-xs uppercase tracking-[0.25em] font-bold text-terracotta mb-5">
                            <span className="inline-block w-8 h-px bg-terracotta align-middle mr-3" />
                            Request a Bid
                        </div>
                        <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tighter font-medium text-ink leading-[1.02]">
                            Send plans. <br /> Get a number <span className="italic font-light text-terracotta">in 48 hours.</span>
                        </h2>
                        <p className="mt-6 text-base md:text-lg text-stone leading-relaxed max-w-md">
                            Drop your scope, square footages, and timeline below. A project manager —
                            not a sales rep — will respond within one business day.
                        </p>

                        <div className="mt-10 space-y-5 border-t border-line pt-8">
                            <a
                                href={`mailto:${COMPANY.email}`}
                                data-testid="contact-email-link"
                                className="flex items-start gap-4 group"
                            >
                                <div className="h-10 w-10 flex items-center justify-center bg-sage text-bone shrink-0">
                                    <Mail size={16} />
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-stone font-semibold">Bids & RFPs</div>
                                    <div className="font-heading text-lg text-ink group-hover:text-terracotta transition-colors">
                                        {COMPANY.email}
                                    </div>
                                </div>
                            </a>
                            <a
                                href={`tel:${COMPANY.phone}`}
                                data-testid="contact-phone-link"
                                className="flex items-start gap-4 group"
                            >
                                <div className="h-10 w-10 flex items-center justify-center bg-sage text-bone shrink-0">
                                    <Phone size={16} />
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-stone font-semibold">Direct Line</div>
                                    <div className="font-heading text-lg text-ink group-hover:text-terracotta transition-colors">
                                        {COMPANY.phone}
                                    </div>
                                </div>
                            </a>
                            <div className="flex items-start gap-4">
                                <div className="h-10 w-10 flex items-center justify-center bg-sage text-bone shrink-0">
                                    <MapPin size={16} />
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-stone font-semibold">Service Region</div>
                                    <div className="font-heading text-lg text-ink">{COMPANY.region}</div>
                                </div>
                            </div>
                        </div>

                        {/* Prequal downloads */}
                        <div className="mt-8 pt-8 border-t border-line">
                            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-terracotta mb-4">
                                Prequalification Pack
                            </div>
                            <div className="space-y-2.5">
                                <a
                                    href="/downloads/H2No-Utah-Capabilities.pdf"
                                    download
                                    data-testid="sidebar-download-capabilities"
                                    className="group flex items-center justify-between gap-4 px-4 py-3 border border-line bg-bone hover:border-ink transition-colors"
                                >
                                    <span className="flex items-center gap-3">
                                        <FileText size={15} className="text-sage" />
                                        <span className="text-sm font-medium text-ink">Capabilities Statement</span>
                                    </span>
                                    <Download size={14} className="text-stone group-hover:text-terracotta transition-colors" />
                                </a>
                                <a
                                    href="/downloads/H2No-Utah-Sample-COI.pdf"
                                    download
                                    data-testid="sidebar-download-coi"
                                    className="group flex items-center justify-between gap-4 px-4 py-3 border border-line bg-bone hover:border-ink transition-colors"
                                >
                                    <span className="flex items-center gap-3">
                                        <ShieldCheck size={15} className="text-sage" />
                                        <span className="text-sm font-medium text-ink">Sample Certificate of Insurance</span>
                                    </span>
                                    <Download size={14} className="text-stone group-hover:text-terracotta transition-colors" />
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* Right: form */}
                    <div className="lg:col-span-7 reveal">
                        {success ? (
                            <div
                                data-testid="contact-success-card"
                                className="border border-line bg-sand p-8 md:p-12"
                            >
                                <div className="text-center">
                                    <div className="mx-auto h-14 w-14 bg-sage text-bone flex items-center justify-center mb-6">
                                        <Check size={26} />
                                    </div>
                                    <h3 className="font-heading text-3xl md:text-4xl tracking-tight font-medium text-ink mb-3">
                                        Inquiry received.
                                    </h3>
                                    <p className="text-stone max-w-md mx-auto">
                                        A project manager will reach out within one business day.
                                        While you wait, grab our capabilities sheet and sample COI:
                                    </p>
                                </div>

                                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <a
                                        href="/downloads/H2No-Utah-Capabilities.pdf"
                                        download
                                        data-testid="download-capabilities"
                                        className="group border border-line bg-bone p-5 hover:border-sage hover:bg-bone transition-colors flex items-start gap-4"
                                    >
                                        <div className="h-11 w-11 shrink-0 bg-sage text-bone flex items-center justify-center">
                                            <FileText size={18} />
                                        </div>
                                        <div className="flex-1 text-left">
                                            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-terracotta mb-1">
                                                Download · PDF
                                            </div>
                                            <div className="font-heading text-base font-medium text-ink leading-tight">
                                                Capabilities Statement
                                            </div>
                                            <div className="text-xs text-stone mt-1">
                                                Scope, NAICS, codes, recent work
                                            </div>
                                        </div>
                                        <Download size={16} className="text-stone group-hover:text-sage transition-colors mt-1" />
                                    </a>
                                    <a
                                        href="/downloads/H2No-Utah-Sample-COI.pdf"
                                        download
                                        data-testid="download-coi"
                                        className="group border border-line bg-bone p-5 hover:border-sage transition-colors flex items-start gap-4"
                                    >
                                        <div className="h-11 w-11 shrink-0 bg-sage text-bone flex items-center justify-center">
                                            <ShieldCheck size={18} />
                                        </div>
                                        <div className="flex-1 text-left">
                                            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-terracotta mb-1">
                                                Download · PDF
                                            </div>
                                            <div className="font-heading text-base font-medium text-ink leading-tight">
                                                Sample COI
                                            </div>
                                            <div className="text-xs text-stone mt-1">
                                                GL · Auto · Umbrella · WC · COI
                                            </div>
                                        </div>
                                        <Download size={16} className="text-stone group-hover:text-sage transition-colors mt-1" />
                                    </a>
                                </div>

                                <div className="mt-6 pt-6 border-t border-line text-center">
                                    <p className="text-xs text-stone mb-4">
                                        Need a project-specific COI? Email {COMPANY.email} with the certificate holder.
                                        For urgent bids, call {COMPANY.phone}.
                                    </p>
                                    <Button
                                        data-testid="contact-submit-another"
                                        onClick={() => setSuccess(false)}
                                        variant="outline"
                                        className="rounded-none border-ink text-ink hover:bg-ink hover:text-bone"
                                    >
                                        Submit another inquiry
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <form
                                onSubmit={onSubmit}
                                data-testid="contact-form"
                                className="border border-line bg-bone p-8 md:p-12"
                            >
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <Field label="Name *" htmlFor="name">
                                        <Input
                                            id="name"
                                            data-testid="contact-input-name"
                                            value={form.name}
                                            onChange={update("name")}
                                            required
                                            className="rounded-none h-12 border-line bg-bone focus-visible:ring-sage focus-visible:ring-offset-0 focus-visible:border-sage"
                                        />
                                    </Field>
                                    <Field label="Company" htmlFor="company">
                                        <Input
                                            id="company"
                                            data-testid="contact-input-company"
                                            value={form.company}
                                            onChange={update("company")}
                                            className="rounded-none h-12 border-line bg-bone focus-visible:ring-sage focus-visible:ring-offset-0 focus-visible:border-sage"
                                        />
                                    </Field>
                                    <Field label="Email *" htmlFor="email">
                                        <Input
                                            id="email"
                                            type="email"
                                            data-testid="contact-input-email"
                                            value={form.email}
                                            onChange={update("email")}
                                            required
                                            className="rounded-none h-12 border-line bg-bone focus-visible:ring-sage focus-visible:ring-offset-0 focus-visible:border-sage"
                                        />
                                    </Field>
                                    <Field label="Phone" htmlFor="phone">
                                        <Input
                                            id="phone"
                                            data-testid="contact-input-phone"
                                            value={form.phone}
                                            onChange={update("phone")}
                                            className="rounded-none h-12 border-line bg-bone focus-visible:ring-sage focus-visible:ring-offset-0 focus-visible:border-sage"
                                        />
                                    </Field>
                                    <div className="md:col-span-2">
                                        <Field label="Project Type *" htmlFor="project_type">
                                            <Select
                                                value={form.project_type}
                                                onValueChange={(v) => setForm((f) => ({ ...f, project_type: v }))}
                                            >
                                                <SelectTrigger
                                                    id="project_type"
                                                    data-testid="contact-input-project-type"
                                                    className="rounded-none h-12 border-line bg-bone focus:ring-sage"
                                                >
                                                    <SelectValue placeholder="Select project type" />
                                                </SelectTrigger>
                                                <SelectContent className="rounded-none">
                                                    {PROJECT_TYPES.map((p) => (
                                                        <SelectItem key={p} value={p} className="rounded-none">
                                                            {p}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </Field>
                                    </div>
                                    <div className="md:col-span-2">
                                        <Field label="Project Details *" htmlFor="details">
                                            <Textarea
                                                id="details"
                                                data-testid="contact-input-details"
                                                value={form.details}
                                                onChange={update("details")}
                                                required
                                                rows={6}
                                                placeholder="Scope, square footages, timeline, site address..."
                                                className="rounded-none border-line bg-bone resize-none focus-visible:ring-sage focus-visible:ring-offset-0 focus-visible:border-sage"
                                            />
                                        </Field>
                                    </div>
                                </div>

                                <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-6 border-t border-line">
                                    <p className="text-xs text-stone max-w-xs">
                                        By submitting, you agree to be contacted about your project.
                                        We don't share leads.
                                    </p>
                                    <Button
                                        type="submit"
                                        data-testid="contact-submit-button"
                                        disabled={submitting}
                                        className="rounded-none bg-terracotta hover:bg-terracotta/90 text-bone h-12 px-8 font-semibold tracking-wide w-full sm:w-auto"
                                    >
                                        {submitting ? (
                                            <>
                                                <Loader2 size={16} className="animate-spin mr-2" />
                                                Sending
                                            </>
                                        ) : (
                                            <>
                                                Submit Inquiry
                                                <Send size={14} className="ml-2" />
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

const Field = ({ label, htmlFor, children }) => (
    <div>
        <Label
            htmlFor={htmlFor}
            className="block text-[10px] uppercase tracking-[0.22em] font-bold text-stone mb-2"
        >
            {label}
        </Label>
        {children}
    </div>
);
